/**
 * aC Men's Hub の画面レイアウト（ルート要素・ナビ・ユーザーメニュー）。
 *
 * `app.tsx` が行数上限（cortex-quality/max-code-lines）に触れたため、
 * ルーティングと Provider の組み立て（app.tsx）から表示の骨格をここへ分離した。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub の共通レイアウト。認証状態に応じた出し分け、ナビゲーション、ユーザーメニューを担う。
 * @graph-connects none
 */

import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@cortex/web-design-system/components/layout/AppShell';
import type {
  SidebarNavGroup,
  SidebarRenderNavLinkArgs,
} from '@cortex/web-design-system/components/layout/Sidebar';
import { Logo } from '@cortex/web-design-system/components/ui/Logo';
import { LanguageToggle, ThemeToggle } from './components/HeaderActions.js';
import { useAuth } from './hooks/useAuth.js';
import { AuthErrorState } from './components/AuthErrorState.js';
import {
  NAV_GROUPS,
  navItemPermissionKeys,
  resolvePagePermissionKey,
  satisfiesPagePermission,
} from './nav.js';
import { usePermissions } from './hooks/usePermissions.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 認証でルートをガードする。未認証は /login、認証済みは Hub 全体を表示。
 * @graph-connects useAuth [calls] Auth state query
 * @graph-connects AuthErrorState [calls] Auth failure fallback
 */
export function RootLayout() {
  const { error, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    if (isLoading || error) {
      return;
    }
    if (!isAuthenticated && !isLoginPage) {
      void navigate({ to: '/login' });
      return;
    }
    if (isAuthenticated && isLoginPage) {
      void navigate({ to: '/' });
    }
  }, [error, isAuthenticated, isLoading, isLoginPage, navigate]);

  if (isLoading && !isLoginPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-base)]">
        <div className="flex flex-col items-center gap-4">
          <div className="loading-spinner" />
          <span className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</span>
        </div>
      </div>
    );
  }

  if (error && !isLoginPage) {
    return <AuthErrorState error={error} />;
  }

  if (!isLoginPage && isAuthenticated) {
    return <AppLayout />;
  }

  return <Outlet />;
}

/**
 * 共有 Sidebar のリンク描画関数。TanStack Router の Link でナビ項目を描画する。
 * @graph-connects none
 */
function renderNavLink({
  item,
  className,
  children,
  onNavigate,
}: SidebarRenderNavLinkArgs): ReactNode {
  return (
    <Link
      to={item.path}
      className={className}
      activeOptions={{ exact: item.path === '/' }}
      onClick={onNavigate}
    >
      {children}
    </Link>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 認証済みユーザー向けの AppShell 描画。ナビ・テーマトグル・ログアウトを組み立てる。
 * @graph-connects useAuth [calls] Auth hook
 * @graph-connects AppShell [calls] 共有レイアウトシェル呼び出し
 */
function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, isLoading: permLoading, hasPermission } = usePermissions();

  useEffect(() => {
    if (permLoading) return;
    const currentPath = location.pathname;
    if (currentPath === '/' || currentPath === '/login' || currentPath === '/forbidden') return;
    const pageKey = resolvePagePermissionKey(currentPath);
    if (!satisfiesPagePermission(pageKey, hasPermission)) {
      void navigate({ to: '/forbidden' });
    }
  }, [permissions, permLoading, location.pathname, hasPermission, navigate]);

  const navGroups: SidebarNavGroup[] = NAV_GROUPS.map((group) => ({
    label: group.labelKey ? t(group.labelKey) : undefined,
    items: group.items
      .filter((item) => satisfiesPagePermission(navItemPermissionKeys(item), hasPermission))
      .map((item) => ({
        key: item.key,
        label: item.mvp === true ? `${t(item.labelKey)} ★` : t(item.labelKey),
        path: item.path,
        icon: item.icon,
      })),
  })).filter((group) => group.items.length > 0);

  return (
    <AppShell
      appName={t('app.name')}
      logo={
        <Link to="/" className="flex items-center gap-2">
          <Logo size="sm" />
        </Link>
      }
      navGroups={navGroups}
      renderNavLink={renderNavLink}
      currentPath={location.pathname}
      headerActions={
        <>
          <LanguageToggle />
          <ThemeToggle />
        </>
      }
      userMenu={
        user ? (
          <UserMenu
            email={user.email}
            onLogout={() => {
              logout();
            }}
          />
        ) : undefined
      }
    >
      <Outlet />
    </AppShell>
  );
}

interface UserMenuProps {
  email: string;
  onLogout: () => void;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header に渡すユーザーメニュー。ログイン中メールアドレスとログアウトボタンを表示する。
 * @graph-connects none
 */
function UserMenu({ email, onLogout }: UserMenuProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <span className="hidden max-w-[200px] truncate text-xs text-[var(--color-text-muted)] sm:inline">
        {email}
      </span>
      <button
        type="button"
        onClick={onLogout}
        className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2 text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-error)] hover:text-[var(--color-error)]"
        aria-label={t('common.logout')}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
